import React, { useState, useRef, useEffect, useContext } from "react";
import { TextField, DialogActions, Box, Button, Dialog, DialogTitle, DialogContent, Avatar } from "@mui/material";
import { ContactsDetContext } from '../../../ContactsDetContext';
import { ToastContainer, toast } from "react-toastify";
import axios from "axios";
import useApi from '../../../Utilities/useAPI';
import Cropper from "react-cropper";
import "cropperjs/dist/cropper.css";
import { MuiTelInput, matchIsValidTel } from 'mui-tel-input'
import frame from "../../../../Assets/img/upload-image.png";
import { useQuantityContext } from "../../../QuantityContext";
import { getConnectedReducer, getAllFoldersReducer, postContactNonuserReducer } from "../../../../Redux/Reducers/ContactReducer";
import { useDispatch } from "react-redux";

const AddNewContact = ({ openAddContact, setOpenAddContact, newContact, setNewContact }) => {
  const apiKey = process.env.REACT_APP_API_KEY;
  const objectId = localStorage.getItem("objectId");
  const headers = {
    'x-api-key': apiKey,
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${objectId}`
  };

  const { setUnverifiedContacts, setUnVerifedCount } = useContext(ContactsDetContext);

  const { callApiFunction } = useApi();

  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const cropperRef = useRef(null);
  const [cropperInitialized, setCropperInitialized] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState('IN'); // Default country
  const dispatch = useDispatch();

  const { handleSuccess, handleInfo } = useQuantityContext();
  // Validation state
  const [errors, setErrors] = useState({
    firstName: false,
    lastName: false,
    email: false,
    phoneNumber: false
  });

  // const { handleInfo } = useQuantityContext();
  const resetContact = () => {
    setNewContact({
      firstName: "",
      lastName: "",
      phoneNumber: "+91",
      email: "",
      profile_url: ""
    });
    setErrors({
      firstName: false,
      lastName: false,
      email: false,
      phoneNumber: false
    });
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const imageUrl = URL.createObjectURL(file);
    setSelectedFile(file);
    setSelectedImage(imageUrl);
    setOpenDialog(true);
  };

  useEffect(() => {
    if (openDialog && selectedImage && !cropperInitialized && cropperRef.current) {
      setCropperInitialized(true);
    }
  }, [openDialog, selectedImage, cropperInitialized]);

  const handleCropperInitialized = (instance) => {
    cropperRef.current = instance;
  };

  const handleCropSave = () => {
    if (!cropperRef.current || !cropperRef.current.getCroppedCanvas) {
      console.error("Cropper not initialized or getCroppedCanvas function not available.");
      return;
    }

    const canvas = cropperRef.current.getCroppedCanvas();

    if (canvas) {
      canvas.toBlob(
        (blob) => {
          const croppedFile = new File([blob], selectedFile.name || 'croppedImage.jpg', {
            type: selectedFile.type || 'image/jpeg',
          });

          // Update selected image state
          setSelectedImage(URL.createObjectURL(croppedFile));

          // Prepare image object for form data
          const imageObject = {
            name: croppedFile.name,
            type: croppedFile.type,
            data: URL.createObjectURL(croppedFile),
          };

          // Update form data with cropped image object
          setNewContact((prevFormData) => ({
            ...prevFormData,
            profile_url: imageObject?.data,
          }));

          // Close dialog after cropping
          setOpenDialog(false);

          // Upload the cropped file
          // handleFileUpload(croppedFile);
        },
        'image/jpeg'
      );
    } else {
      console.error("Canvas not available after cropping.");
    }
  };

  // Function to handle file upload to server
  const handleFileUpload = async (file) => {
    if (!file) {
      console.error('No file to upload.');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/uploadpublicblob/generate-sas-url`, formData, {
        headers: { headers },
      });

      // Assuming your response structure is like { sasUrl: 'some_url' }
      const sasUrl = response.data.url;
      setNewContact((prevFormData) => ({
        ...prevFormData,
        profile_url: sasUrl,
      }));
    } catch (error) {
      console.error('Error uploading file:', error.message);
      handleInfo('Error uploading file. Please check the console for details.');
    }
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setNewContact((prevFormData) => ({
      ...prevFormData,
      [name]: value,
    }));

    // Validate input
    if (value.trim() === "") {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [name]: true
      }));
    } else {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [name]: false
      }));
    }
  };

  const handlePhoneChange = (value, name) => {
    setNewContact(prevState => ({
      ...prevState,
      [name]: value
    }));

    const isValid = matchIsValidTel(value, { defaultCountry: selectedCountry });
    // Validate phone number
    if (!isValid) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [name]: true
      }));
    } else {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [name]: false
      }));
    }
  };

  // Handle country code change
  const handleCountryChange = (countryCode) => {
    setSelectedCountry(countryCode);
    // Validate the current phone number with the new country code
    const isValid = matchIsValidTel(newContact.phoneNumber, { defaultCountry: countryCode });
    // Validate phone number
    if (!isValid) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        phoneNumber: true
      }));
    } else {
      setErrors((prevErrors) => ({
        ...prevErrors,
        phoneNumber: false
      }));
    }
  };

  const formatPhoneNumber = (phoneNumber) => {
    // Extract country code and main number with a regular expression
    const match = phoneNumber.match(/^(\+\d{1,3})\s?(.*)$/);
    if (match) {
      const countryCode = match[1]; // country code part
      const mainNumber = match[2].replace(/\s+/g, "");
      return `${countryCode} ${mainNumber}`;
    }
    return phoneNumber; // returns the original if it doesn't match
  };

  const validateEmail = (email) => {
    // Simple regex for email validation
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  const handleSavePeople = async () => {
    // Validate inputs first
    const isEmailValid = validateEmail(newContact.email);

    // Create a fresh error object based on current values
    const newErrors = {
      email: !isEmailValid,
      // Add more validations here if needed (e.g., name, phone, etc.)
      // firstName: newContact.firstName.trim() === '',
      // phoneNumber: newContact.phoneNumber.trim() === '',
    };

    // Set the errors state
    setErrors(newErrors);

    // Check if any errors exist
    if (Object.values(newErrors).some(error => error)) {
      handleInfo("Please fill in all required fields.");
      return;
    }

    const apiPayload = {
      ...newContact,
      phoneNumber: formatPhoneNumber(newContact.phoneNumber)
    };

    const formData = new FormData();
    formData.append('firstName', newContact.firstName);
    formData.append('lastName', newContact.lastName);
    formData.append('email', newContact.email);
    formData.append('phoneNumber', newContact.phoneNumber);
    if (newContact?.profile_url) {
      const response = await fetch(newContact.profile_url);
      const blob = await response.blob();
      formData.append('profile_pic', blob);
    }

    try {
      const response = await dispatch(postContactNonuserReducer(formData));
      // console.log("responseresponse response", response);
      if (response?.payload?.data?.statusCode === 200) {
        await dispatch(getConnectedReducer());

        if (response?.payload?.data?.message?.trim() === "Found existing user and connected.") {
          setOpenAddContact(false);
          resetContact();
          handleInfo("Found existing user and connected");
        } else {
          const newMember = {
            id: -1,
            contact_ref_id: 100000,
            img: newContact.profile_url,
            is_favourite: false,
            name: `${newContact.firstName} ${newContact.lastName}`,
            role: 'Pending Verification',
            email: newContact.email,
            verified: false,
          };

          setUnverifiedContacts(prev => [...prev, newMember]);
          setUnVerifedCount(prev => prev + 1);
          setOpenAddContact(false);
          resetContact();
          dispatch(getAllFoldersReducer());
          handleSuccess("New contact Added Successfully.");
        }
      } else if (response?.payload?.data?.statusCode === 400) {
        handleInfo("User with E-mail ID or Phone Number already exists on Platform.");
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error("User already exists with this email or phone number.", {
        position: 'top-right',
        autoClose: 5000,
      });
      resetContact();
      setOpenAddContact(false);
    }
  };

  // Check if Save button should be enabled
  const isSaveDisabled =
    !newContact.firstName.trim() ||
    !newContact.lastName.trim() ||
    !newContact.email.trim() ||
    !newContact.phoneNumber.trim() ||
    Object.values(errors).some(error => error);


  return (
    <>
      {openAddContact && (
        <div className="lightbox-overlay">
          <div className="lightbox-content-2 thin-lightbox">
            <div className="active-component-container">
              <div className="d-flex justify-content-between">
                <div className="card-edit-info-heading">
                  <h6 className="text-left fw-bold">Add Contact</h6>
                  <span className="sub-text-lightbox">
                    Enter the details manually below to save the contact. (This will be saved as a non-verified contact until the user confirms their details.)
                  </span>
                </div>
                <button className="lightbox-close pointer" onClick={() => { setOpenAddContact(false); resetContact(); }}>&times;</button>
              </div>

              <div className="my-2 user-details-container">
                <div className={selectedImage ? "avatar-container" : "avatar-placeholder"}>
                  {selectedImage ? (
                    <img src={selectedImage} alt="Uploaded" className="uploaded-image" />
                  ) : (
                    <img src={frame} alt="Upload" onClick={() => document.getElementById("fileInput").click()} />
                  )}
                </div>

                <input
                  type="file"
                  id="fileInput"
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={handleFileSelect}
                />

                <div className="upload-button-container">
                  <button
                    className="next-btn upload-button"
                    onClick={() => document.getElementById("fileInput").click()}
                  >
                    Browse
                  </button>
                </div>
              </div>

              <div className="mx-5">
                <TextField
                  label="First Name"
                  variant="outlined"
                  fullWidth
                  margin="normal"
                  name="firstName"
                  value={newContact.firstName}
                  onChange={handleInputChange}
                  className="my-1"
                  error={errors.firstName}
                  helperText={errors.firstName ? 'First name is required.' : ''}
                />
                <TextField
                  label="Last Name"
                  variant="outlined"
                  fullWidth
                  margin="normal"
                  name="lastName"
                  value={newContact.lastName}
                  onChange={handleInputChange}
                  className="my-1"
                  error={errors.lastName}
                  helperText={errors.lastName ? 'Last name is required.' : ''}
                />
                <MuiTelInput
                  name="phoneNumber"
                  value={newContact.phoneNumber}
                  onChange={(value) => handlePhoneChange(value, 'phoneNumber')}
                  onCountryChange={handleCountryChange}
                  required
                  style={{ width: "100%" }}
                  className="my-1"
                  error={errors.phoneNumber}
                  helperText={errors.phoneNumber ? 'Enter Valid Phone number.' : ''}
                />
                <TextField
                  label="Email"
                  variant="outlined"
                  fullWidth
                  margin="normal"
                  name="email"
                  value={newContact.email}
                  onChange={handleInputChange}
                  className="my-1"
                  error={errors.email}
                  helperText={errors.email ? 'Enter Valid Email ID' : ''}
                />
              </div>

              <div className="mt-4 d-flex justify-content-end">
                <Button className="me-3" sx={{ color: "#0044C6" }} onClick={() => { resetContact(); setOpenAddContact(false); }}>Cancel</Button>
                <Button
                  variant="contained"
                  sx={{ backgroundColor: '#0044C6' }}
                  onClick={handleSavePeople}
                  disabled={isSaveDisabled} // Disable Save button if validation fails
                >
                  SAVE
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>Crop Image</DialogTitle>
        <DialogContent>
          <div style={{ width: '100%', height: '400px', position: 'relative' }}>
            <Cropper
              ref={cropperRef}
              src={selectedImage}
              style={{ height: '100%', width: '100%' }}
              initialAspectRatio={1}
              guides={true}
              cropBoxMovable={true}
              cropBoxResizable={true}
              crossOrigin="anonymous"
              dragMode="crop"
              viewMode={1}
              background={false}
              responsive={true}
              autoCrop={false}
              autoCropArea={1}
              minCropBoxHeight={20}
              minCropBoxWidth={20}
              checkOrientation={false}
              onInitialized={handleCropperInitialized}
            />
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCropSave}>Save</Button>
        </DialogActions>
      </Dialog>

      <ToastContainer toastClassName="email-toast-success" />
    </>
  );
};

export default AddNewContact;